import SubmitApplicationResponse from '../../responses/acquisitionApiBff/submit-application.json';
import UpdateApplicationResponse from '../../responses/acquisitionApiBff/update-application.json';
import ApplicationStatusResponse from '../../responses/acquisitionApiBff/status-of-application.json';
import ConsentManagementResponse from '../../responses/acquisitionApiBff/consent-management.json';
import StepupAuthResponse from '../../responses/acquisitionApiBff/stepup-authorization.json';
import OffersResponse from '../../responses/acquisitionApiBff/offers.json';
import BankRoutingResponse from '../../responses/acquisitionApiBff/bank-routing.json';
import SecuredCardFundingResponse from '../../responses/acquisitionApiBff/secured-card-funding.json';
import SecuredCardFundingAttemptResponse from '../../responses/acquisitionApiBff/secured-card-funding-attempt.json';
import { NewApplicationStatus } from '../../types/responses/NewApplicationStatus';
import LimitedEsignResponse from '../../responses/acquisitionApiBff/limited-esign.json';
import { UUIDv4 } from '../cob-utils';
type APIEndpoint = {
    mock: object;
    endpoint: string;
    method?: 'GET' | 'get' | 'POST' | 'post' | 'PUT' | 'put' | 'PATCH' | 'patch';
    correlationId?: string;
    getMockResponse?: (searchParams: URLSearchParams) => object;
};

/* 
    This mapping is for the new acquisition API that's being developed initially for Project S.
    For now, it will be specific to the PHA Secured flow, but over time will include the other flows
        while IT is transitioning to just use these new endpoints instead of the ones used in the
        "api-url-map.ts" file.
*/

const API_MAPPING: { [key: string]: APIEndpoint } = {
    'submit-application': {
        endpoint: '/applications',
        correlationId: UUIDv4(),
        mock: SubmitApplicationResponse,
        method: 'POST',
        getMockResponse: (searchParams) => {
            const response = { ...SubmitApplicationResponse };
            const offerId = searchParams.get('offerId');
            
            if (!offerId || !offerId.startsWith('test-')) {
                return response;
            }
            
            if (offerId === 'test-offer-expired') {
                response.status = 500;
            }
            if (offerId === 'test-offer-declined') {
                response.data.applicationStatus.applicationStatus = 'Declined';
                response.data.letter.templateCode = '0071';
            }
            
            const stepUpTypes = ['test-offer-kiq-approved', 'test-offer-kiq-inprocess', 'test-offer-mitek', 'test-offer-otp'];
            if (stepUpTypes.some(type => offerId.toLowerCase() === type)) {
                response.data.applicationStatus.applicationStatus = 'StepUp';
                response.data.stepUpAuthorization.stepUpType = offerId.toLowerCase().includes('kiq') ? 'KIQ' : offerId.toUpperCase().replace('TEST-OFFER-', '');
            }
            
            return response;
        },
    },

    'application-search': {
        endpoint: '/applications:search',
        correlationId: UUIDv4(),
        mock: SubmitApplicationResponse,
        method: 'POST',
    },

    'update-application': {
        endpoint: '/applications/{applicationId}',
        correlationId: UUIDv4(),
        mock: UpdateApplicationResponse,
        method: 'PATCH',
    },

    'communications': {
        endpoint: '/communications',
        correlationId: UUIDv4(),
        mock: null,
        method: 'POST',
    },

    'application-status': {
        endpoint: '/applications/status',
        mock: ApplicationStatusResponse,
        method: 'POST',
        getMockResponse: (searchParams) => {
            const response = JSON.parse(JSON.stringify(ApplicationStatusResponse));
            const testStatus = searchParams.get('testStatus');
            if (!testStatus) {
                return response;
            }
            
            const app = response.data.applications[0] as NewApplicationStatus;
            switch (testStatus) {
                case 'approved-ready-to-fund':
                    app.applicationStatus.applicationStatus = 'approved';
                    app.applicationStatus.statusDescription = 'Ready To Fund';
                    app.fundingAttempts.achAttemptsExceeded = false;
                    app.fundingAttempts.debitAttemptsExceeded = false;
                    break;
                case 'approved-ready-to-board':
                    app.applicationStatus.applicationStatus = 'approved';
                    app.applicationStatus.statusDescription = 'Ready To Board';
                    app.fundingAttempts.achAttemptsExceeded = false;
                    app.fundingAttempts.debitAttemptsExceeded = false;
                    break;
                case 'declined-bank-suppression':
                    app.applicationStatus.applicationStatus = 'Declined';
                    app.applicationStatus.statusDescription = 'bank account suppression list';
                    app.fundingAttempts.achAttemptsExceeded = true;
                    app.fundingAttempts.debitAttemptsExceeded = true;
                    break;
                case 'ready-to-fund-ews-failed':
                    app.applicationStatus.applicationStatus = 'Ready To Fund';
                    app.applicationStatus.statusDescription = 'EWS Check Failed';
                    app.fundingAttempts.achAttemptsExceeded = true;
                    app.fundingAttempts.debitAttemptsExceeded = false;
                    break;
                case 'ready-to-fund-switch-to-debit':
                    app.applicationStatus.applicationStatus = 'Ready To Fund';
                    app.applicationStatus.statusDescription = 'Switch To Debit';
                    app.fundingAttempts.achAttemptsExceeded = true;
                    app.fundingAttempts.debitAttemptsExceeded = false;
                    break;
                case 'error-internal':
                    app.applicationStatus.applicationStatus = 'Error';
                    app.applicationStatus.statusDescription = 'Request Failed Internal Error';
                    app.fundingAttempts.achAttemptsExceeded = false;
                    app.fundingAttempts.debitAttemptsExceeded = false;
                    break;
                case 'ready-to-fund-ach-failed-not-exceeded':
                    app.applicationStatus.applicationStatus = 'Ready To Fund';
                    app.applicationStatus.statusDescription = 'Switch to Debit';
                    app.fundingAttempts.achAttemptsExceeded = true;
                    app.fundingAttempts.debitAttemptsExceeded = false;
                    break;
                case 'declined-nsf-exceeded':
                    app.applicationStatus.applicationStatus = 'Declined';
                    app.applicationStatus.statusDescription = 'Failed Funding NSF';
                    app.fundingAttempts.achAttemptsExceeded = true;
                    app.fundingAttempts.debitAttemptsExceeded = true;
                    break;
                case 'ready-to-fund-nsf-not-exceeded':
                    app.applicationStatus.applicationStatus = 'Ready To Fund';
                    app.applicationStatus.statusDescription = 'Failed Funding NSF';
                    app.fundingAttempts.achAttemptsExceeded = true;
                    app.fundingAttempts.debitAttemptsExceeded = false;
                    break;
                case 'ready-to-fund-debit-verification-failed':
                    app.applicationStatus.applicationStatus = 'Ready To Fund';
                    app.applicationStatus.statusDescription = 'Failed Debit Funding Verification';
                    app.fundingAttempts.achAttemptsExceeded = true;
                    app.fundingAttempts.debitAttemptsExceeded = false;
                    break;
                case 'declined-debit-verification-exceeded':
                    app.applicationStatus.applicationStatus = 'Declined';
                    app.applicationStatus.statusDescription = 'Failed Debit Funding Verification';
                    app.fundingAttempts.achAttemptsExceeded = true;
                    app.fundingAttempts.debitAttemptsExceeded = true;
                    break;
                default:
                    break;
            }
            return response;
        },
    },

    'stepup-authorization': {
        endpoint: '/applications/{applicationId}/stepup',
        correlationId: UUIDv4(),
        mock: StepupAuthResponse,
        method: 'POST',
        getMockResponse: (searchParams) => {
            const response =  JSON.parse(JSON.stringify(StepupAuthResponse));
            const offerId = searchParams.get('offerId');

            if (!offerId || !offerId.startsWith('test-')) {
                return response;
            }

            if (offerId === 'test-offer-decline') {
                response.data.applicationStatus.applicationStatus = 'Declined';
            } else if (offerId === 'test-offer-pending') {
                response.data.applicationStatus.applicationStatus = 'Pending';
            } else {
                const stepUpType = offerId.toLowerCase().includes('kiq') ? 'KIQ' : offerId.toUpperCase().replace('TEST-OFFER-', '');
                response.data.stepUpAuthorization.stepUpType = stepUpType;
            }
            return response;
        },
    },

    'consent-management': {
        endpoint: '/applications/{applicationId}/consents',
        correlationId: UUIDv4(),
        mock: ConsentManagementResponse,
        method: 'POST',
    },

    'offers': {
        endpoint: '/offers',
        mock: OffersResponse,
        method: 'POST',
    },

    'routing-verification': {
        endpoint: '/banks',
        mock: BankRoutingResponse,
        method: 'GET',
    },

    'secured-card-funding': {
        endpoint: '/applications/{applicationId}/funds',
        mock: SecuredCardFundingResponse,
        method: 'POST',
    },

    'secured-card-funding-attempt': {
        endpoint: '/applications/{applicationId}/fundingattempt',
        mock: SecuredCardFundingAttemptResponse,
        method: 'GET',
    },

    'debit-card-funding-attempt': {
        endpoint: '/applications/{applicationId}/fundingattempt/ACI',
        mock: SecuredCardFundingAttemptResponse,
        method: 'POST',
    },

    'limited-esign': {
        endpoint: '/applications/{applicationsId}/limitedesign',
        mock: LimitedEsignResponse,
        method: 'POST',
    },

};

export default API_MAPPING;
